import { invisibleUnicodeCharacters, NGWords } from './string.ruleset';

/**
 * Segment a string into an array of graphemes.
 * @param input
 * @returns
 */
export function segmentString(input: string) {
  // If supported use Intl's segmenter.
  // Need to cast to 'any' because TS doesn't know about Intl.Segmenter.
  const intl = Intl;
  if (intl?.Segmenter) {
    const segmenter = new intl.Segmenter();
    if (segmenter.segment) {
      const segmentsArray = Array.from(segmenter.segment(input)) as {
        segment: string;
      }[];
      return segmentsArray.map(({ segment }) => segment);
    }
  }

  // Otherwise use `Array.from`. Which doesn't cover all cases.
  return Array.from(input);
}

/**
 * Get the real length of a string. Considers graphemes.
 * @param input
 * @returns
 */
export function getRealLength(input: string) {
  return segmentString(input).length;
}

/**
 * Add line breaks to japanese text. Will remove existing line breaks.
 * @param text - text to be broken
 * @param desiredLineLength - desired line length
 * @returns - wrapped text, lines can be longer than desiredLineLength if there is no space to break
 */
export function breakJapaneseText(
  text: string | undefined,
  desiredLineLength: number = 16,
) {
  // If text is undefined, return undefined.
  if (!text) {
    return text;
  }

  // Remove line breaks from the original text.
  text = text.replace(/\n/g, ' ');

  // Characters that cannot be broken.
  const blacklistedCharacters =
    ',)]｝、〕〉》」』】〙〗〟’”｠»ゝゞーァィゥェォッャュョヮヵヶぁぃぅぇぉっゃゅょゎゕゖㇰㇱㇲㇳㇴㇵㇶㇷㇸㇹㇷ゚ㇺㇻㇼㇽㇾㇿ々〻‐゠–〜～?!‼⁇⁈⁉・:;/。.';

  // Current character index.
  let cursor = desiredLineLength;

  // Do not modify the original string.
  let result = text.slice();

  while (cursor < result.length) {
    // While current character is blacklisted, move the cursor forward.
    while (blacklistedCharacters.includes(result[cursor])) cursor++;
    // Hit the end of the string, break.
    if (cursor >= result.length) break;

    // Add line break.
    result =
      result.slice(0, cursor).trim() + // Trim to remove trailing spaces.
      '\n' + // Add line break.
      result.slice(cursor, result.length).trim(); // Trim to remove leading spaces.

    // Move the cursor to the desired end of next line.
    cursor = result.lastIndexOf('\n') + desiredLineLength + 1;
  }

  // Return new text.
  return result;
}

/**
 * Check if message is in japanese.
 * This can yield false positives, if the message is in english but contains japanese characters.
 * @param input
 * @returns
 */
export function isJapanese(input: string | undefined) {
  return !!input?.match(/[\u3040-\u30ff]/);
}

/**
 * Capitalize string.
 * @param input
 */
export function capitalize(input: string) {
  return input.charAt(0).toUpperCase() + input.slice(1);
}

/**
 * Remove invisible unicode characters.
 * @param input
 * @returns
 */
export function removeInvisibleCharacters(input: string, replacement = '?') {
  // Join without separators: a character class needs none, and joining
  // with '|' made the literal pipe character itself match.
  const expression = `[${invisibleUnicodeCharacters.join('')}]`;
  return input.replace(new RegExp(expression, 'g'), replacement);
}

/**
 * Add space to NG words.
 * @param input
 * @returns
 */
export function maskNGWords(input: string) {
  return NGWords.reduce(
    (acc, word) => acc.replace(word, `${word.charAt(0)} ${word.slice(1)}`),
    input,
  );
}

/**
 * Get chatbot safe pet name.
 * @param input
 * @returns
 */
export function cleanPetName(input: string) {
  return maskNGWords(removeInvisibleCharacters(input));
}

/**
 * Strips non-Japanese friendly characters from a string.
 * Used for external integrations that don't support emoji etc.
 * @param input
 */
export function stripNonJapaneseFriendly(input: string) {
  const regex = new RegExp(
    // Characters that are not:
    '[^' +
      // ASCII characters
      '\x00-\x7F' +
      // Japanese characters
      '\\p{Script=Hiragana}\\p{Script=Katakana}\\p{Script=Han}' +
      // Match one or more
      ']+',
    // Global and unicode-aware match
    'gu',
  );

  // Strip characters matching the above regex
  return input.replace(regex, '');
}

/**
 * Validate that a string is a valid semantic version (dot-separated numbers).
 * @param version - version string to validate
 * @returns true if valid semver format, false otherwise
 */
export function isValidVersion(version: string): boolean {
  if (!version || typeof version !== 'string') return false;
  const parts = version.split('.');
  return parts.every((part) => {
    if (part === '') return false;
    const num = parseInt(part, 10);
    return !isNaN(num) && num >= 0 && String(num) === part;
  });
}

/**
 * Compare two semantic version strings.
 * @param a - version a
 * @param b - version b
 * @returns - 1 if a > b, -1 if a < b, 0 if a === b
 */
export function compareVersions(a: string, b: string) {
  const aParts = a.split('.');
  const bParts = b.split('.');
  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aPart = parseInt(aParts[i] ?? '0', 10);
    const bPart = parseInt(bParts[i] ?? '0', 10);
    if (aPart > bPart) {
      return 1;
    }
    if (aPart < bPart) {
      return -1;
    }
  }
  return 0;
}
